import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, YStack, XStack, Text, View, Button } from "tamagui";
import { AxiosResponse } from "axios";
import liff from "@line/liff";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RiArrowLeftSLine, RiPhoneLine, RiShieldCheckLine } from "react-icons/ri";
import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import RadiusCard from "@components/layout/RadiusCard";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { OTPInputBlocks } from "@components/form/OTPInputBlocks";
import { colors } from "@styles/colors";
import api, { handleRequest } from "@services/axios/apiManager";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { getSendOtpErrorMessage } from "@utils/otpError";
import appToast from "@components/Toast";

import { maskPhone } from "@utils/func/maskPhone";

/** 本地 skip-LIFF：略過真實 OTP API，任意 5 碼即通過（正式環境不受影響）。 */
const DEV_MOCK_OTP = process.env.NODE_ENV === "development" && process.env.REACT_APP_SKIP_LIFF_LOGIN === "true";

/**
 * 手機 + OTP 登入頁。OTP 收發沿用既有 send-phone-otp / verify-phone-otp。
 * 註：目前尚無「以手機/OTP 換取登入 session」的後端 endpoint，故驗證通過後的登入
 * 為 mock（直接進 /home）。待後端提供登入 API 後替換 loginByPhone 內容即可。
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const { lineAccessToken } = useLineAuth();
  const { setLoading } = useUtilsStore();
  const [sendState, setSendState] = useState<{ success?: boolean } | null>(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const formSchema = z.object({
    phone: z
      .string()
      .min(1, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" })
      .refine(validateTaiwanPhone, { message: "請輸入有效的手機號碼 (09開頭共10碼) 或市話 (含區碼)" }),
    otp: z.string().optional(),
  });
  type FormData = z.infer<typeof formSchema>;

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { phone: "", otp: "" },
  });

  //@ 登入（mock）：TODO(api) 換成後端「以 phoneVerifiedTicket 換取 session」的登入 endpoint。
  const loginByPhone = () => {
    appToast.success("登入成功");
    navigate("/home", { replace: true });
  };

  //@ 發送驗證碼
  const onSendOtp = async (data: FormData) => {
    if (DEV_MOCK_OTP) {
      setSendState({ success: true });
      setCountdown(60);
      setValue("otp", "");
      appToast.success("（本地 mock）驗證碼已發送，任意 5 碼即可");
      return;
    }
    if (!lineAccessToken) {
      liff.login();
      return;
    }
    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/send-phone-otp", { accessToken: lineAccessToken, phone: data?.phone || "" }, { isAuth: false }),
      successCallback: (res: AxiosResponse) => {
        setSendState(res.data.data);
        setCountdown(60);
        setValue("otp", "");
        appToast.success("驗證碼已發送");
      },
      errorCallback: (error: unknown) => setError("phone", { type: "manual", message: getSendOtpErrorMessage(error) }),
      setLoading,
    });
  };

  //@ 驗證 OTP → 登入
  const onVerifyOtp = async (otpValue: string) => {
    if (!otpValue || otpValue.length !== 5) {
      setError("otp", { type: "manual", message: "請輸入 5 位數驗證碼" });
      return;
    }
    if (DEV_MOCK_OTP) {
      loginByPhone();
      return;
    }
    if (!lineAccessToken) return;
    const phone = getValues("phone");
    handleRequest({
      asyncRequest: () =>
        api.post("/app-api/line/verify-phone-otp", { accessToken: lineAccessToken, phone, otpCode: otpValue }, { isAuth: false }),
      successCallback: () => loginByPhone(),
      errorCallback: () => setError("otp", { type: "manual", message: "驗證碼錯誤，請重新輸入" }),
      setLoading,
    });
  };

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={() => navigate(-1)} cursor="pointer" mb="$2">
          <View mr="$1">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color="rgba(255,255,255,0.75)" fontSize={13}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800">
          登入帳戶
        </Text>
        <Text color="rgba(255,255,255,0.75)" fontSize={13} mt="$1">
          {sendState?.success ? `已傳送驗證碼至 ${maskPhone(getValues("phone"))}` : "歡迎回來！"}
        </Text>
      </Header>

      <RadiusContentWrapper>
        {!sendState?.success ? (
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="手機號碼（🇹🇼 +886）" icon={<RiPhoneLine color={colors.primary} />} />
              <YStack mt="$3">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} mb={4}>
                  請輸入您註冊時使用的手機號碼
                </Text>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      fontSize="$4"
                      placeholder="輸入手機號碼"
                      keyboardType="phone-pad"
                      textAlign="center"
                      letterSpacing={3}
                      value={value}
                      onBlur={onBlur}
                      borderColor={errors.phone ? "$red10" : undefined}
                      borderWidth={errors.phone ? 1 : 0}
                      onChangeText={(text: string) => onChange(text)}
                    />
                  )}
                />
                {errors.phone && (
                  <Text color="$red10" fontSize={14} mt="$2">
                    {errors.phone.message}
                  </Text>
                )}
              </YStack>
            </RadiusCard>
            <XStack
              className="primary-bg-gradient"
              borderRadius={20}
              p={15}
              ai="center"
              jc="center"
              cursor="pointer"
              onPress={handleSubmit(onSendOtp)}>
              <Text color="white" fontSize={16} fontWeight="bold">
                取得驗證碼
              </Text>
            </XStack>
          </YStack>
        ) : (
          <YStack gap={15}>
            <RadiusCard>
              <RadiusBoxLabel label="驗證碼" icon={<RiShieldCheckLine color={colors.primary} />} />
              <YStack mt="$3" alignItems="center">
                <Text fontSize={14} fontWeight="bold" color={colors.gray} alignSelf="flex-start">
                  請輸入 5 位數驗證碼
                </Text>
                <Text fontSize={14} color={colors.gray} my="$3" fontWeight="bold">
                  已傳送至 {maskPhone(getValues("phone"))}
                </Text>
                <Controller
                  control={control}
                  name="otp"
                  render={({ field: { onChange, value } }) => (
                    <OTPInputBlocks value={value || ""} onChange={onChange} onComplete={(val) => onVerifyOtp(val)} error={errors.otp?.message} />
                  )}
                />
                {errors.otp && (
                  <Text color="$red10" fontSize={14} mt="$2" alignSelf="center">
                    {errors.otp.message}
                  </Text>
                )}
                <YStack mt="$2" alignItems="center">
                  <Button
                    chromeless
                    size="$3"
                    color={countdown > 0 ? colors.gray : colors.primary}
                    disabled={countdown > 0}
                    onPress={() => onSendOtp(getValues())}>
                    {countdown > 0 ? `重新傳送驗證碼 (${countdown}s)` : "重新傳送驗證碼"}
                  </Button>
                </YStack>
              </YStack>
            </RadiusCard>
            <Button
              chromeless
              size="$3"
              color={colors.primary}
              onPress={() => {
                setSendState(null);
                setValue("otp", "");
              }}>
              重新輸入手機號碼
            </Button>
          </YStack>
        )}
      </RadiusContentWrapper>
    </YStack>
  );
};

export default LoginPage;
